import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../../lib/api'
import { KpiCard, Section } from '../../components/ui'
import { DemoAccessPanel } from '../../components/DemoAccessPanel'
import type { AdminOverview as AdminOverviewType } from '../../lib/types'

export default function AdminOverview() {
  const [data, setData] = useState<AdminOverviewType | null>(null)
  const [includeDemo, setIncludeDemo] = useState(false)

  useEffect(() => {
    api(`/admin/overview${includeDemo ? '?include_demo=true' : ''}`).then(setData)
  }, [includeDemo])

  if (!data) return <div className="page-loading">Loading platform overview…</div>

  return (
    <div>
      <div className="page-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1>ATUUNE platform overview</h1>
          <div className="ph-sub">Cross-organization totals, Session 4 retention, and recent activity.</div>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input type="checkbox" checked={includeDemo} onChange={(e) => setIncludeDemo(e.target.checked)} />
          Include demo network
        </label>
      </div>

      <DemoAccessPanel />

      <div className="kpis">
        <KpiCard label="Organizations" value={data.total_organizations} />
        <KpiCard label="Providers" value={data.total_providers} />
        <KpiCard label="Credentialed providers" value={data.credentialed_providers} />
        <KpiCard label="Patients / care seekers" value={data.total_patients} />
        <KpiCard label="Active matches" value={data.active_matches} hint="patient attached to an active case" />
        <KpiCard label="Session 1 attended" value={data.session_1_attended} />
        <KpiCard label="Session 4 reached" value={data.session_4_reached} />
        <KpiCard label="Active billable seats" value={data.active_billable_seats} />
        <KpiCard label="Outcome fee events" value={data.outcome_fee_events} />
      </div>

      <Section title="Continuity funnel" note={data.session_4_conversion_rate != null ? `${Math.round(data.session_4_conversion_rate * 100)}% Session 4 conversion` : undefined}>
        <div className="kpis">
          <KpiCard label="Total cases" value={data.total_cases} />
          <KpiCard label="Active cases" value={data.active_cases} />
          <KpiCard label="Dropped before Session 4" value={data.dropped_before_session_4} />
        </div>
      </Section>

      <Section title="Drop-off by session stage" note="where care fails">
        <div className="kpis">
          {Object.entries(data.drop_off_by_stage).map(([k, v]) => (
            <KpiCard key={k} label={k.replace(/_/g, ' ')} value={v} />
          ))}
        </div>
      </Section>

      <Section title="Open risk alerts by band">
        <div className="kpis">
          {Object.entries(data.open_risk_alerts_by_band).map(([band, count]) => (
            <KpiCard key={band} label={band} value={count} />
          ))}
        </div>
      </Section>

      <Section title="Recent platform activity" note={`${data.recent_activity.length} shown`}>
        <div className="row-list">
          {data.recent_activity.map((e) => (
            <div key={e.id} className="row">
              <span><span className="badge badge-blue">{e.event_type}</span> {e.entity_type}{e.entity_id ? ` · ${e.entity_id.slice(0, 8)}` : ''}</span>
              <span className="muted">{new Date(e.created_at).toLocaleString()}</span>
            </div>
          ))}
          {data.recent_activity.length === 0 && <div className="empty">No platform activity recorded yet.</div>}
        </div>
        <Link to="/admin/event-logs" className="muted" style={{ fontSize: 13 }}>View full event log &rarr;</Link>
      </Section>

      <p className="legal-note">
        Revenue, cohort, and predictive-factor panels are intentionally not shown here — they require real
        billing and outcome-trained data that doesn't exist yet. We don't fabricate numbers for this view.
      </p>
    </div>
  )
}
